import { useCallback, useRef } from "react";
import {
  Popover as ArkPopover,
  Portal,
  type PopoverInteractOutsideEvent,
} from "@ark-ui/react";
import classnames from "classnames";

import Icon from "@/components/icon/icon";
import PopoverStyles from "@/components/popover/popover.module.css";
import type { TimeSeriesParameterOption } from "@/features/shared/widgets/map/time-series/time-series-parameter.types";
import DraggableCard from "@/features/widget-forms/forms/weather-model-map/components/draggable-card/draggable-card";
import { SortableItem, SortableList } from "@/features/admin/components/sortable-list";
import { useTranslation } from "@/lib/i18n";

import type { TimeSeriesParameterEntry } from "./display-group-board-sync";
import triggerStyles from "./display-group-parameter-list-editor.module.css";
import styles from "./display-group-parameters-popover.module.css";

type DisplayGroupParametersPopoverProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  parameterCount: number;
  parameterNamesLabel: string;
  parameterIds: string[];
  poolEntries: TimeSeriesParameterEntry[];
  parameters: TimeSeriesParameterOption[];
  onReorder: (fromIndex: number, toIndex: number) => void;
  onRemove: (parameterId: string) => void;
};

function resolveParameterLabel(
  parameterId: string,
  poolEntries: TimeSeriesParameterEntry[],
  parameters: TimeSeriesParameterOption[],
): string {
  const entry = poolEntries.find((e) => e.parameterId === parameterId);
  const catalog = parameters.find((p) => p.id === parameterId);
  return entry?.label.trim() || catalog?.label || parameterId;
}

/**
 * Anchored parameter editor popover on a marker-design tile.
 * Uses Popover.Trigger (not a headless external trigger) so Zag can wire
 * aria-controls, exclude, and proxyTabFocus — required for Safari DnD + outside close.
 */
export function DisplayGroupParametersPopover({
  open,
  onOpenChange,
  parameterCount,
  parameterNamesLabel,
  parameterIds,
  poolEntries,
  parameters,
  onReorder,
  onRemove,
}: DisplayGroupParametersPopoverProps) {
  const { t } = useTranslation();
  const contentRef = useRef<HTMLDivElement>(null);

  const handleOpenChange = useCallback(
    (details: { open: boolean }) => {
      onOpenChange(details.open);
    },
    [onOpenChange],
  );

  const handleInteractOutside = useCallback((event: PopoverInteractOutsideEvent) => {
    const target = event.detail.target;
    if (target instanceof Node && contentRef.current?.contains(target)) {
      event.preventDefault();
    }
  }, []);

  return (
    <ArkPopover.Root
      open={open}
      onOpenChange={handleOpenChange}
      onInteractOutside={handleInteractOutside}
      portalled
      positioning={{ placement: "bottom-start", gutter: 6, flip: true }}
    >
      <ArkPopover.Trigger asChild>
        <button
          type="button"
          className={triggerStyles.trigger}
          aria-label={t(
            "admin.widgets.fields.observations_map.marker_designs.edit_parameters",
            { count: parameterCount },
          )}
          onMouseDown={(event) => event.stopPropagation()}
        >
          <span className={triggerStyles.triggerLabel}>
            {parameterCount} · {parameterNamesLabel}
          </span>
          <Icon name="list" size={14} />
        </button>
      </ArkPopover.Trigger>
      <Portal>
        <ArkPopover.Positioner>
          <ArkPopover.Content
            ref={contentRef}
            className={classnames(
              PopoverStyles.popoverContent,
              PopoverStyles.popoverNested,
              styles.panelContent,
            )}
          >
            <div className={styles.panelHeader}>
              <p className={styles.panelHint}>
                {t(
                  "admin.widgets.fields.observations_map.marker_designs.parameters_dialog_hint",
                )}
              </p>
            </div>
            <div className={styles.panelBody}>
              <SortableList
                onReorder={onReorder}
                groupId="display-group-parameters-popover"
              >
                {parameterIds.map((parameterId, index) => {
                  const label = resolveParameterLabel(parameterId, poolEntries, parameters);
                  return (
                    <SortableItem key={parameterId} index={index}>
                      <DraggableCard
                        onRemove={() => onRemove(parameterId)}
                        removeLabel={t(
                          "admin.widgets.fields.observations_map.marker_designs.remove_parameter",
                          { name: label },
                        )}
                      >
                        <span className={styles.fullLabel}>{label}</span>
                      </DraggableCard>
                    </SortableItem>
                  );
                })}
              </SortableList>
            </div>
          </ArkPopover.Content>
        </ArkPopover.Positioner>
      </Portal>
    </ArkPopover.Root>
  );
}
